/**
 * ModernClientsTab - Enhanced clients management with modern design
 */
import React, { useState, useMemo, useEffect } from 'react';
import { 
  Mail, Users, Filter, Search, Download, Trash2, TrendingUp, 
  Calendar, User, MailPlus, Copy, Check, Pencil, Phone 
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import AdminCard from './AdminCard';
import AdminCardActions from './AdminCardActions';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useAdmin } from './AdminHooks';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Label } from '../ui/label';
import { adminAPI } from './AdminUtils';
import { cn } from '../../lib/utils';

const ClientStatCard = ({ icon: Icon, title, value, tint }) => (
  <Card className="bg-slate-900/40 border-slate-700">
    <CardContent className="flex justify-center items-center p-4 h-24">
      <div className="flex flex-col gap-2 items-center">
        <span className={cn("inline-flex justify-center items-center w-8 h-8 rounded-md", tint)}>
          <Icon className="w-4 h-4" />
        </span>
        <div className="text-center">
          <div className="text-[11px] tracking-wide uppercase text-slate-400">{title}</div>
          <div className="text-xl font-bold leading-tight text-white">{value}</div>
        </div>
      </div>
    </CardContent>
  </Card>
);

const SourceBadge = ({ source }) => {
  const sourceConfig = {
    reservation: { 
      label: 'Réservation', 
      className: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      icon: Calendar 
    },
    newsletter: { 
      label: 'Newsletter', 
      className: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      icon: MailPlus 
    },
    both: { 
      label: 'Les deux', 
      className: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      icon: Users 
    }
  };

  const config = sourceConfig[source] || { 
    label: source, 
    className: 'bg-gray-500/20 text-gray-400',
    icon: User 
  };
  const Icon = config.icon;

  return (
    <Badge className={cn("flex items-center gap-1", config.className)}>
      <Icon size={12} />
      {config.label}
    </Badge>
  );
};

const ModernClientsTab = () => {
  const { clients, events, loading, copyEmailsToClipboard, openDeleteClientModal, setAdminActiveTab, openComposeEmail } = useAdmin();
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    try { return window.matchMedia('(max-width: 768px)').matches; } catch { return false; }
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const mq = window.matchMedia('(max-width: 768px)');
      const handler = (e) => setIsMobile(e.matches);
      if (mq.addEventListener) mq.addEventListener('change', handler);
      else mq.onchange = handler;
      return () => {
        if (mq.removeEventListener) mq.removeEventListener('change', handler);
        else mq.onchange = null;
      };
    } catch {}
  }, []);
  const [searchTerm, setSearchTerm] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [sortBy, setSortBy] = useState('vol_desc');
  const [volumeFilter, setVolumeFilter] = useState('all');
  const [copied, setCopied] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [editName, setEditName] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [viewMode, setViewMode] = useState('table');

  const filteredClients = useMemo(() => {
    if (!clients?.clients) return [];
    
    const filtered = clients.clients.filter(client => {
      const matchesSearch = !searchTerm || 
        client.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (client.name && client.name.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesSource = sourceFilter === 'all' || client.source === sourceFilter;
      const matchesVolume = volumeFilter === 'all' || (client.volumes || []).includes(parseInt(volumeFilter));
      return matchesSearch && matchesSource && matchesVolume;
    });
    const getLatestVol = (c) => {
      const vols = c.volumes || [];
      return vols.length ? Math.max(...vols) : 0;
    };
    const sorted = [...filtered].sort((a, b) => {
      if (sortBy === 'vol_asc') return getLatestVol(a) - getLatestVol(b);
      if (sortBy === 'vol_desc') return getLatestVol(b) - getLatestVol(a);
      return 0;
    });
    return sorted;
  }, [clients, searchTerm, sourceFilter]);

  const handleCopyEmails = async () => {
    await copyEmailsToClipboard();
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const exportToCSV = () => {
    const csvContent = [
      ['Email', 'Téléphone', 'Nom', 'Source', 'Volumes'],
      ...filteredClients.map(c => [c.email, c.phone || '', c.name || '', c.source, (c.volumes || []).join(' | ')])
    ].map(row => row.join(',')).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'clients.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const stats = React.useMemo(() => {
    if (!clients?.clients) return { total: 0, bySource: {} };
    
    const bySource = clients.clients.reduce((acc, client) => {
      acc[client.source] = (acc[client.source] || 0) + 1;
      return acc;
    }, {});
    
    return {
      total: clients.total,
      bySource
    };
  }, [clients]);
  const [showEmails, setShowEmails] = useState(false);
  const emailCount = useMemo(() => {
    try {
      const s = clients?.emailsFormatted || '';
      const list = s.split(',').map(x => x.trim()).filter(Boolean);
      return Array.from(new Set(list)).length;
    } catch {
      return 0;
    }
  }, [clients?.emailsFormatted]);

  const openEditClient = (client) => {
    setEditClient(client);
    setEditName(client.name || '');
    setEditPhone(client.phone || '');
    setEditOpen(true);
  };

  const handleSaveClient = async () => {
    if (!editClient) return;
    try {
      await adminAPI.put(`/clients/${encodeURIComponent(editClient.email)}`, {
        name: editName,
        phone: editPhone,
      });
      setEditOpen(false);
      setEditClient(null);
      setEditName('');
      setEditPhone('');
      // Optionally refresh via admin hook
      try { setAdminActiveTab('clients'); } catch {}
    } catch (e) {
      // silently fail UI-wise
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Gestion des Clients</h2>
          <p className="text-slate-400">
            {stats.total} client(s) unique(s) dans la base de données
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={exportToCSV}
            className="border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white"
          >
            <Download size={16} className="mr-2" />
            Exporter
          </Button>
          <Button
            onClick={handleCopyEmails}
            className={cn(
              "transition-all duration-200",
              copied 
                ? "text-white bg-green-600 hover:bg-green-700" 
                : "text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
            )}
          >
            {copied ? <Check size={16} className="mr-2" /> : <Copy size={16} className="mr-2" />}
            {copied ? "Copié !" : "Copier Emails"}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <ClientStatCard icon={Users} title="Total Clients" value={stats.total} tint="bg-slate-700/40" />
        <ClientStatCard icon={Calendar} title="Par Réservation" value={stats.bySource.reservation || 0} tint="bg-blue-600/20 text-blue-300" />
        <ClientStatCard icon={MailPlus} title="Par Newsletter" value={stats.bySource.newsletter || 0} tint="bg-purple-600/20 text-purple-300" />
        <ClientStatCard icon={Users} title="Les Deux" value={stats.bySource.both || 0} tint="bg-emerald-600/20 text-emerald-300" />
      </div>

      {/* Email Export Section */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white">Export des Emails</CardTitle>
          <CardDescription className="text-slate-400">
            Liste de tous les emails des clients pour les communications
          </CardDescription>
        </CardHeader>
        <CardContent>
          {clients && clients.total > 0 && (
            <div className="space-y-4">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex gap-2 items-center">
                  <div className="text-sm text-slate-400">Emails copiés</div>
                  <Badge className="bg-slate-700 text-slate-200 border-slate-600">{emailCount}</Badge>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowEmails((v) => !v)}
                    className="border-slate-600 text-slate-300 hover:text-white"
                  >
                    {showEmails ? "Masquer" : "Voir tous"}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleCopyEmails}
                    className={cn(
                      "border-slate-600",
                      copied 
                        ? "text-green-400 hover:text-green-300" 
                        : "text-slate-300 hover:text-white"
                    )}
                  >
                    {copied ? <Check size={16} /> : <Copy size={16} />}
                  </Button>
                </div>
              </div>
              {showEmails && (
                <div className="p-3 rounded-md border bg-slate-900/40 border-slate-700">
                  <div className="font-mono text-sm break-words text-slate-300">
                    {clients.emailsFormatted}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Search and Filters - no container */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-3 top-1/2 w-4 h-4 transform -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Rechercher par nom ou email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 text-white bg-slate-900 border-slate-700 placeholder-slate-400"
          />
        </div>
        <Select value={sourceFilter} onValueChange={setSourceFilter}>
          <SelectTrigger className="w-[140px] bg-slate-900 border-slate-700 text-white">
            <Filter size={16} className="mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700">
            <SelectItem value="all" className="text-white">Sources</SelectItem>
            <SelectItem value="reservation" className="text-white">Réservation</SelectItem>
            <SelectItem value="newsletter" className="text-white">Newsletter</SelectItem>
            <SelectItem value="both" className="text-white">Les deux</SelectItem>
          </SelectContent>
        </Select>
        <Select value={volumeFilter} onValueChange={setVolumeFilter}>
          <SelectTrigger className="w-[160px] bg-slate-900 border-slate-700 text-white">
            <Filter size={16} className="mr-2" />
            <SelectValue placeholder="Vol." />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700">
            <SelectItem value="all" className="text-white">Édition</SelectItem>
            {Array.from(new Set((events || []).map(e => e.volume))).sort((a,b) => b-a).map(v => (
              <SelectItem key={v} value={String(v)} className="text-white">Vol.{v}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex rounded-lg border bg-slate-900 border-slate-700">
          <Button
            size="sm"
            variant={viewMode === 'table' ? 'default' : 'ghost'}
            onClick={() => setViewMode('table')}
            className={cn(
              "rounded-r-none",
              viewMode === 'table' && "bg-blue-600 text-white"
            )}
          >
            Tableau
          </Button>
          <Button
            size="sm"
            variant={viewMode === 'cards' ? 'default' : 'ghost'}
            onClick={() => setViewMode('cards')}
            className={cn(
              "rounded-l-none",
              viewMode === 'cards' && "bg-blue-600 text-white"
            )}
          >
            Cartes
          </Button>
        </div>
      </div>

      {/* Clients Table / Cards */}
      {loading ? (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-8 text-center">
            <div className="mx-auto mb-4 w-8 h-8 rounded-full border-b-2 border-blue-400 animate-spin"></div>
            <p className="text-slate-400">Chargement des clients...</p>
          </CardContent>
        </Card>
      ) : viewMode === 'table' ? (
        <Card className="overflow-hidden bg-slate-800/50 border-slate-700">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700 hover:bg-slate-800/30">
                  <TableHead className="text-slate-300 border-x border-slate-700">Email</TableHead>
                  <TableHead className="text-slate-300 border-x border-slate-700">Téléphone</TableHead>
                  <TableHead className="text-slate-300 border-x border-slate-700">Nom</TableHead>
                  <TableHead className="text-slate-300 border-x border-slate-700">Source</TableHead>
                  <TableHead className="text-center text-slate-300 border-x border-slate-700">Ajouté</TableHead>
                  <TableHead className="text-center text-slate-300 border-x border-slate-700">Vol(s)</TableHead>
                  <TableHead className="text-center text-slate-300 border-x border-slate-700">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredClients.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="py-8 text-center text-slate-400">
                      Aucun client trouvé
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredClients.map((client, index) => (
                    <TableRow 
                      key={index}
                      className="transition-colors border-slate-700 hover:bg-slate-800/30"
                    >
                      <TableCell className="font-medium text-white border-x border-slate-700">{client.email}</TableCell>
                      <TableCell className="text-slate-300 border-x border-slate-700">{client.phone || '—'}</TableCell>
                      <TableCell className="text-slate-300 border-x border-slate-700">{client.name || '—'}</TableCell>
                      <TableCell className="border-x border-slate-700">
                        <SourceBadge source={client.source} />
                      </TableCell>
                      <TableCell className="text-center text-slate-300 border-x border-slate-700">
                        {(() => {
                          const s = client.addedAt;
                          if (!s) return '—';
                          try {
                            const d = new Date(s);
                            return d.toLocaleString('fr-BE', { dateStyle: 'short', timeStyle: 'short' });
                          } catch {
                            return String(s);
                          }
                        })()}
                      </TableCell>
                      <TableCell className="text-center text-slate-300 border-x border-slate-700">{(client.volumes || []).join(', ') || '—'}</TableCell>
                      <TableCell className="text-center border-x border-slate-700">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => { setAdminActiveTab('notifications'); openComposeEmail(client.email); }}
                          className="text-slate-400 hover:text-blue-400 hover:bg-blue-500/10"
                          title="Envoyer un email"
                        >
                          <MailPlus size={14} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openEditClient(client)}
                          className="text-slate-400 hover:text-white hover:bg-slate-700"
                          title="Modifier le client"
                        >
                          <Pencil size={14} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openDeleteClientModal(client)}
                          className="text-slate-400 hover:text-red-400 hover:bg-red-500/10"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {filteredClients.map((client, index) => (
            <AdminCard key={index}>
              <CardHeader className="pb-3">
                <div className="flex gap-3 justify-between items-start">
                  <div className="min-w-0">
                    <div className="flex gap-2 items-center">
                      <CardTitle className="text-lg text-white truncate">{client.name || '—'}</CardTitle>
                      <SourceBadge source={client.source} />
                    </div>
                    <div className="flex flex-wrap gap-2 items-center mt-1 text-sm text-slate-400">
                      <span className="inline-flex items-center">
                        <Mail size={14} className="mr-1" />
                        {client.email}
                      </span>
                      {client.phone && (
                        <span className="inline-flex items-center">
                          <span className="mx-1 text-slate-600">•</span>
                          <Phone size={14} className="mr-1" />
                          {client.phone}
                        </span>
                      )}
                      {client.addedAt && (
                        <span className="inline-flex items-center">
                          <span className="mx-1 text-slate-600">•</span>
                          <Calendar size={14} className="mr-1 text-slate-400" />
                          {(() => {
                            const s = client.addedAt;
                            try {
                              const d = new Date(s);
                              return d.toLocaleString('fr-BE', { dateStyle: 'short', timeStyle: 'short' });
                            } catch {
                              return String(s);
                            }
                          })()}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="p-3 min-w-0 rounded-lg border border-slate-700 bg-slate-800/40">
                    <div className="text-xs tracking-wide uppercase text-slate-400">Vol(s)</div>
                    <div className="mt-1 text-slate-200">
                      {(client.volumes || []).length
                        ? (client.volumes || []).join(', ')
                        : '—'}
                    </div>
                  </div>
                  <div className="p-3 min-w-0 rounded-lg border border-slate-700 bg-slate-800/40">
                    <div className="text-xs tracking-wide uppercase text-slate-400">Vol(s)</div>
                    <div className="mt-1 text-slate-200">
                      {(client.volumes || []).length
                        ? (client.volumes || []).join(', ')
                        : '—'}
                    </div>
                  </div>
                </div>
              </CardContent>
              <AdminCardActions>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => { setAdminActiveTab('notifications'); openComposeEmail(client.email); }}
                  className="text-slate-300 hover:text-blue-400 hover:bg-blue-500/10"
                  title="Envoyer un email"
                >
                  <MailPlus size={14} />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => openEditClient(client)}
                  className="text-slate-300 hover:text-white hover:bg-slate-700"
                  title="Modifier"
                >
                  <Pencil size={14} />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => openDeleteClientModal(client)}
                  className="text-slate-300 hover:text-red-400 hover:bg-red-500/10"
                  title="Supprimer"
                >
                  <Trash2 size={14} />
                </Button>
              </AdminCardActions>
            </AdminCard>
          ))}
        </div>
      )}
      <Dialog open={editOpen} onOpenChange={(o) => { 
        setEditOpen(o); 
        if (!o) { setEditClient(null); setEditName(''); setEditPhone(''); }
      }}>
        <DialogContent className="text-white bg-slate-950 border-slate-800">
          <DialogHeader>
            <DialogTitle>Modifier le client</DialogTitle>
            <DialogDescription>Mettre à jour les informations du client.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label className="text-slate-300">Email</Label>
              <Input value={editClient?.email || ''} readOnly className="text-white bg-slate-900 border-slate-700" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Nom</Label>
              <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="text-white bg-slate-900 border-slate-700" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Téléphone</Label>
              <Input value={editPhone} onChange={(e) => setEditPhone(e.target.value)} className="text-white bg-slate-900 border-slate-700" />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => { setEditOpen(false); setEditClient(null); setEditName(''); setEditPhone(''); }} className="border-slate-700 text-slate-300 hover:text-white hover:bg-slate-700">Annuler</Button>
              <Button onClick={handleSaveClient} className="text-white bg-blue-600 hover:bg-blue-700">Enregistrer</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

ModernClientsTab.propTypes = {};

export default ModernClientsTab;
